import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  CarFrontIcon,
  CheckIcon,
  ClipboardListIcon,
  GaugeIcon,
  HistoryIcon,
  MoreHorizontalIcon,
  PackageIcon,
  PencilIcon,
  PlusIcon,
  ShieldCheckIcon,
  ShoppingCartIcon,
  Trash2Icon,
  WrenchIcon,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { DocumentList, ImageGallery } from "@/components/app/attachments";
import { ChassisCodeChip } from "@/components/app/chassis-codes";
import { EmptyState } from "@/components/app/empty-state";
import { LinksPanel } from "@/components/app/links";
import { Markdown } from "@/components/app/markdown";
import { Page, PageHeader, SectionTitle } from "@/components/app/page";
import { RoadworthinessBadge, useRoadworthinessDate } from "@/components/app/roadworthiness";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/misc";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AddToOrderDialog } from "@/features/orders/add-to-order-dialog";
import { VehicleSheet } from "@/features/vehicles/vehicle-sheet";
import { WorkOrderRow } from "@/features/work-orders/work-order-row";
import { WorkOrderSheet } from "@/features/work-orders/work-order-sheet";
import { useCurrentVehicle } from "@/lib/current-vehicle";
import { useFormat } from "@/lib/format";
import { type RouterOutputs, useTRPC } from "@/lib/trpc";
import { ServiceHistoryEntryExtra, useVehicleTabs } from "@/modules";

export const Route = createFileRoute("/vehicles/$vehicleId")({
  component: VehiclePage,
});

type Vehicle = RouterOutputs["vehicles"]["get"];

function VehiclePage() {
  const { vehicleId } = Route.useParams();
  const { t } = useTranslation();
  const trpc = useTRPC();
  const format = useFormat();
  const roadworthinessDate = useRoadworthinessDate();
  const navigate = useNavigate();
  const { data: vehicle, isLoading } = useQuery(trpc.vehicles.get.queryOptions({ id: vehicleId }));
  const current = useCurrentVehicle();
  const isCurrent = current.vehicleId === vehicleId;
  const { setVehicleId } = current;
  const [editing, setEditing] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const archive = useMutation(trpc.vehicles.setArchived.mutationOptions());
  const remove = useMutation(trpc.vehicles.delete.mutationOptions());
  const moduleTabs = useVehicleTabs(vehicleId);

  if (isLoading || !vehicle) {
    return (
      <Page>
        <Skeleton className="mb-6 h-56 rounded-2xl" />
        <Skeleton className="h-64 rounded-xl" />
      </Page>
    );
  }

  const specs: [string, ReactNode][] = [
    [t("vehicles.make"), vehicle.make],
    [t("vehicles.model"), vehicle.model],
    [t("vehicles.variant"), vehicle.variant],
    [t("vehicles.modelYear"), vehicle.modelYear ? String(vehicle.modelYear) : null],
    [t("vehicles.productionDate"), vehicle.productionDate],
    [
      t("vehicles.chassisCode"),
      vehicle.chassisCode ? (
        <ChassisCodeChip key="chassis" code={vehicle.chassisCode} className="text-xs" />
      ) : null,
    ],
    [t("vehicles.engineCode"), vehicle.engineCode],
    [t("vehicles.transmission"), vehicle.transmission],
    [
      t("vehicles.driveType"),
      vehicle.driveType ? t(`vehicles.driveTypes.${vehicle.driveType}`) : null,
    ],
    [t("vehicles.paintCode"), vehicle.paintCode],
    [t("vehicles.licensePlate"), vehicle.licensePlate],
    [
      t("roadworthiness.next"),
      vehicle.roadworthiness ? roadworthinessDate(vehicle.roadworthiness.dueOn) : null,
    ],
  ];

  return (
    <Page>
      <PageHeader
        crumbs={[{ label: t("nav.vehicles"), to: "/vehicles" }]}
        title={vehicle.name}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {[vehicle.make, vehicle.model, vehicle.variant].filter(Boolean).join(" ")}
            {vehicle.mileage !== null ? (
              <span className="flex items-center gap-1 tabular">
                <GaugeIcon className="size-3.5" />
                {format.mileage(vehicle.mileage, vehicle.mileageUnit)}
              </span>
            ) : null}
            {vehicle.vin ? <span className="font-mono text-xs">{vehicle.vin}</span> : null}
            {vehicle.roadworthiness ? (
              <span className="flex items-center gap-1.5">
                <span className="flex items-center gap-1" title={t("roadworthiness.next")}>
                  <ShieldCheckIcon className="size-3.5" />
                  <span className="sr-only">{t("roadworthiness.next")}:</span>
                  {roadworthinessDate(vehicle.roadworthiness.dueOn)}
                </span>
                <RoadworthinessBadge due={vehicle.roadworthiness} />
              </span>
            ) : null}
          </span>
        }
        actions={
          <>
            {vehicle.archived ? null : (
              <Button
                variant="outline"
                aria-pressed={isCurrent}
                aria-label={isCurrent ? t("currentVehicle.current") : t("currentVehicle.workOn")}
                title={isCurrent ? t("currentVehicle.current") : t("currentVehicle.workOn")}
                onClick={() => setVehicleId(isCurrent ? null : vehicle.id)}
              >
                {isCurrent ? <CheckIcon /> : <CarFrontIcon />}
                {/* Icon only on narrower screens, so the title keeps its room. */}
                <span className="hidden lg:inline">
                  {isCurrent ? t("currentVehicle.current") : t("currentVehicle.workOn")}
                </span>
              </Button>
            )}
            <Button variant="outline" onClick={() => setEditing(true)}>
              <PencilIcon />
              {t("common.edit")}
            </Button>
            <Button onClick={() => setPlanning(true)}>
              <PlusIcon />
              {t("workOrders.new")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label={t("common.more")}>
                  <MoreHorizontalIcon />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={() => archive.mutate({ id: vehicle.id, archived: !vehicle.archived })}
                >
                  {vehicle.archived ? <ArchiveRestoreIcon /> : <ArchiveIcon />}
                  {vehicle.archived ? t("common.unarchive") : t("common.archive")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem destructive onSelect={() => setConfirmDelete(true)}>
                  <Trash2Icon />
                  {t("common.delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      {vehicle.archived ? (
        <div className="mb-5 flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
          <ArchiveIcon className="size-4" />
          {t("vehicles.archivedBanner")}
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        <div className="min-w-0">
          <Tabs defaultValue="work">
            <TabsList>
              <TabsTrigger value="work">
                {t("vehicles.openWork")}
                {vehicle.workOrders.length ? (
                  <span className="rounded-full bg-muted px-1.5 text-[11px] text-muted-foreground tabular">
                    {vehicle.workOrders.length}
                  </span>
                ) : null}
              </TabsTrigger>
              <TabsTrigger value="history">{t("vehicles.history")}</TabsTrigger>
              <TabsTrigger value="details">{t("common.details")}</TabsTrigger>
              {moduleTabs.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
            <TabsContent value="work" className="flex flex-col gap-6">
              {vehicle.shortages.length ? (
                <Shortages vehicle={vehicle} onOrder={() => setOrdering(true)} />
              ) : null}
              {vehicle.workOrders.length ? (
                <Card className="divide-y divide-border">
                  {vehicle.workOrders.map((wo) => (
                    <WorkOrderRow key={wo.id} wo={wo} showVehicle={false} />
                  ))}
                </Card>
              ) : (
                <EmptyState
                  compact
                  icon={ClipboardListIcon}
                  title={t("vehicles.openWorkEmpty")}
                  action={
                    <Button variant="outline" onClick={() => setPlanning(true)}>
                      <PlusIcon />
                      {t("workOrders.new")}
                    </Button>
                  }
                />
              )}
            </TabsContent>
            <TabsContent value="history">
              <ServiceHistory vehicle={vehicle} />
            </TabsContent>
            <TabsContent value="details" className="flex flex-col gap-7">
              <dl className="grid grid-cols-2 gap-x-6 gap-y-4 rounded-xl border border-border bg-surface p-5 sm:grid-cols-3">
                {specs.map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-xs text-muted-foreground">{label}</dt>
                    <dd className="mt-0.5 text-sm font-medium">{value ?? "–"}</dd>
                  </div>
                ))}
              </dl>
              {vehicle.description ? (
                <section>
                  <SectionTitle>{t("common.description")}</SectionTitle>
                  <Markdown>{vehicle.description}</Markdown>
                </section>
              ) : null}
              <div className="grid gap-7 md:grid-cols-2">
                <section>
                  <SectionTitle>{t("common.documents")}</SectionTitle>
                  <DocumentList
                    ownerType="vehicle"
                    ownerId={vehicle.id}
                    attachments={vehicle.attachments}
                  />
                </section>
                <section>
                  <SectionTitle>{t("common.links")}</SectionTitle>
                  <LinksPanel ownerType="vehicle" ownerId={vehicle.id} links={vehicle.links} />
                </section>
              </div>
            </TabsContent>
            {moduleTabs.map((tab) => (
              <TabsContent key={tab.value} value={tab.value}>
                {tab.content}
              </TabsContent>
            ))}
          </Tabs>
        </div>
        <aside className="flex flex-col gap-4 lg:order-first lg:col-start-2 lg:row-start-1">
          <ImageGallery
            ownerType="vehicle"
            ownerId={vehicle.id}
            attachments={vehicle.attachments}
          />
          {vehicle.history.length ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-border bg-surface p-3">
                <div className="text-xs text-muted-foreground">{t("vehicles.totalSpent")}</div>
                <div className="mt-1 font-semibold tabular">
                  {format.money(vehicle.totalSpent.totalMinor, vehicle.totalSpent.currency)}
                </div>
              </div>
              <div className="rounded-xl border border-border bg-surface p-3">
                <div className="text-xs text-muted-foreground">{t("vehicles.history")}</div>
                <div className="mt-1 font-semibold tabular">{vehicle.history.length}</div>
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      <VehicleSheet open={editing} onOpenChange={setEditing} vehicle={vehicle} />
      <WorkOrderSheet
        open={planning}
        onOpenChange={setPlanning}
        vehicleId={vehicle.id}
        onSaved={(id) =>
          void navigate({ to: "/work-orders/$workOrderId", params: { workOrderId: id } })
        }
      />
      <AddToOrderDialog
        open={ordering}
        onOpenChange={setOrdering}
        items={vehicle.shortages
          .filter((s) => s.missing > 0)
          .map((s) => ({
            partId: s.partId,
            name: s.partName,
            unit: s.unit,
            qty: s.missing,
            workOrderId: s.workOrders[0]?.id ?? null,
          }))}
      />
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t("common.deleteConfirmTitle", { name: vehicle.name })}
        description={t("vehicles.deleteBody")}
        confirmLabel={t("common.delete")}
        destructive
        pending={remove.isPending}
        onConfirm={() =>
          remove.mutate(
            { id: vehicle.id },
            {
              onSuccess: () => {
                toast.success(t("vehicles.deleted"));
                void navigate({ to: "/vehicles" });
              },
            },
          )
        }
      />
    </Page>
  );
}

function Shortages({ vehicle, onOrder }: { vehicle: Vehicle; onOrder: () => void }) {
  const { t } = useTranslation();
  const format = useFormat();
  const anyMissing = vehicle.shortages.some((s) => s.missing > 0);
  return (
    <Card className="overflow-hidden">
      <div className="flex flex-col gap-3 border-b border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-sm font-semibold">{t("vehicles.shortages")}</h3>
          <p className="text-xs text-muted-foreground">{t("vehicles.shortagesBody")}</p>
        </div>
        {anyMissing ? (
          <Button size="sm" onClick={onOrder}>
            <ShoppingCartIcon />
            {t("vehicles.orderMissing")}
          </Button>
        ) : null}
      </div>
      <div className="divide-y divide-border">
        {vehicle.shortages.map((s) => (
          <Link
            key={s.partId}
            to="/inventory/$partId"
            params={{ partId: s.partId }}
            className="flex items-center gap-3 px-4 py-2.5 text-sm transition hover:bg-muted/50"
          >
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">{s.partName}</div>
              <div className="truncate text-xs text-muted-foreground">
                {s.workOrders.map((w) => w.title).join(", ")}
              </div>
            </div>
            {s.onOrder > 0 ? (
              <Badge tone="transit">
                {t("availabilityDetail.fromIncoming", { qty: format.qty(s.onOrder, s.unit) })}
              </Badge>
            ) : null}
            {s.missing > 0 ? (
              <Badge tone="missing">
                {t("availabilityDetail.missing", { qty: format.qty(s.missing, s.unit) })}
              </Badge>
            ) : null}
          </Link>
        ))}
      </div>
    </Card>
  );
}

function ServiceHistory({ vehicle }: { vehicle: Vehicle }) {
  const { t } = useTranslation();
  const format = useFormat();
  if (!vehicle.history.length)
    return <EmptyState compact icon={HistoryIcon} title={t("vehicles.historyEmpty")} />;
  return (
    <ol className="relative flex flex-col gap-4 pl-6 before:absolute before:top-2 before:bottom-2 before:left-[7px] before:w-px before:bg-border">
      {vehicle.history.map((entry) => (
        <li key={entry.workOrderId} className="relative">
          <span className="absolute top-4 -left-6 flex size-[15px] items-center justify-center rounded-full border-2 border-background bg-ok ring-1 ring-ok/30" />
          <Link
            to="/work-orders/$workOrderId"
            params={{ workOrderId: entry.workOrderId }}
            className="block rounded-xl border border-border bg-surface p-4 shadow-xs transition hover:shadow-md"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="font-medium">{entry.title}</span>
              <span className="text-xs text-muted-foreground tabular">
                {format.date(entry.completedOn)}
                {entry.mileage !== null
                  ? ` · ${format.mileage(entry.mileage, vehicle.mileageUnit)}`
                  : ""}
              </span>
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {t(`workOrderType.${entry.type}`)} ·{" "}
              {t("vehicles.partsUsed", {
                count: entry.parts.filter((p) => p.consumesStock).length,
              })}
              {entry.cost.totalMinor > 0
                ? ` · ${format.money(entry.cost.totalMinor, entry.cost.currency)}`
                : ""}
            </div>
            <ServiceHistoryEntryExtra vehicleId={vehicle.id} workOrderId={entry.workOrderId} />
            {entry.parts.length ? (
              <ul className="mt-3 flex flex-col gap-1 border-t border-border pt-3">
                {entry.parts.map((p) => (
                  <li
                    key={`${p.partId}-${p.consumesStock}`}
                    className="flex items-center gap-2 text-sm"
                  >
                    {p.consumesStock ? (
                      <PackageIcon className="size-3.5 shrink-0 text-muted-foreground" />
                    ) : (
                      <WrenchIcon className="size-3.5 shrink-0 text-muted-foreground" />
                    )}
                    <span className="min-w-0 flex-1 truncate">{p.name}</span>
                    {p.number ? (
                      <span className="hidden truncate font-mono text-xs text-muted-foreground sm:block">
                        {p.number}
                      </span>
                    ) : null}
                    <span className="shrink-0 text-xs tabular">
                      {format.qty(p.qtyUsed, p.unit)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </Link>
        </li>
      ))}
    </ol>
  );
}
